import './Filters.scss';
import {
  type CapitalAccountsStoreState,
  isOnlyOneFundAndInvestor,
} from '../../store/slices';
import GoBackButton from '../GoBackButton';
import classNames from 'classnames';
import { NestedOrganizationsSelector } from 'components/NestedOrganizationsSelector/NestedOrganizationsSelector';
import { useRelativePathsToBasePath } from 'hooks';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation,
  useParams } from 'react-router-dom';
import { isMobile, isTablet } from 'services/mobile';
import { type CapitalAccountFund } from 'types';

const VALID_PATHS_FOR_FILTERS = [
  '/firm/documents/*',
];

const VALID_PATHS_FOR_ACCOUNT_NAME = [
  '/firm/capital-accounts/*',
];

const SCROLL_THRESHOLD = 0.2;

const getStickyStyles = ({
  clientHeight,
  isTabletDevice,
  scrollHeight,
  scrollPosition,
  scrollProgress,
  validCapitalAccountsPath,
}: {
  clientHeight: number,
  isTabletDevice: boolean,
  scrollHeight: number,
  scrollPosition: number,
  scrollProgress: number,
  validCapitalAccountsPath: boolean,
}) => {
  if (isTabletDevice) {
    return {};
  }

  if (validCapitalAccountsPath) {
    return {
      height: 'auto',
    };
  }

  return {
    backgroundColor: scrollPosition > SCROLL_THRESHOLD
      ? `rgba(255, 255, 255, ${scrollPosition * 4})`
      : 'rgba(255, 255, 255, 0)',

    boxShadow:
          clientHeight === scrollHeight || scrollPosition < SCROLL_THRESHOLD
            ? '0 0.25em 0.5em 0 rgba(0, 0, 0, 0)'
            : '0 0.25em 0.5em 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04), 0 0.375em 2.5em 0 rgba(38, 50, 56, 0.05)',

    height: `${5 - scrollProgress}rem`,
  };
};

const Filters = () => {
  const params = useParams();
  const mobile = isMobile();
  const location = useLocation();
  const accountId = params['*']?.split('/')[2];
  const fundId = params['*']?.split('/')[1];

  const { valid } = useRelativePathsToBasePath(...VALID_PATHS_FOR_FILTERS);
  const {
    valid: validCapitalAccountsPath,
  } = useRelativePathsToBasePath(...VALID_PATHS_FOR_ACCOUNT_NAME);
  const isAccountRoute = validCapitalAccountsPath && Boolean(accountId);

  // tracking scroll position and calculating scroll progress, code tracks how far the user has scrolled for optimizing animation
  const [scrollProgress, setScrollProgress] = useState(0);
  const clientHeight = document.querySelector('.app')?.clientHeight as number;
  const scrollHeight = document.querySelector('.app')?.scrollHeight as number;
  const scrollTop = document.querySelector('.app')?.scrollTop as number;
  const scrollPosition = scrollTop / (scrollHeight - clientHeight);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = document.querySelector('.app')?.scrollTop;
      const maxScroll = 100;
      if (scrollY || scrollY === 0 && clientHeight === scrollHeight) {
        const progress = Math.min(scrollY / maxScroll, 1);
        setScrollProgress(progress);
      }
    };

    window.addEventListener('DOMMouseScroll', handleScroll);
    window.onwheel = handleScroll;
    window.ontouchmove = handleScroll;
    document.onkeydown = handleScroll;

    return () => window.removeEventListener('DOMMouseScroll', handleScroll);
  }, []);

  const fundName = useSelector<{
    capitalAccounts: CapitalAccountsStoreState,
  }>(
    (state) => state
      .capitalAccounts
      .capitalAccountsData
      ?.find((fund) => fund._id === fundId)?.legalName) as string;

  const capitalAccountsData = useSelector<{
    capitalAccounts: CapitalAccountsStoreState,
  }>(
    (state) => state
      .capitalAccounts
      .capitalAccountsData,
  ) as CapitalAccountFund[] | undefined;

  const fundNameIfSingleFund = useSelector<{
    capitalAccounts: CapitalAccountsStoreState,
  }>(
    (state) => state
      .capitalAccounts
      .capitalAccountsData
      ?.[0].legalName) as string;

  return (
    <div
      className={classNames('sticky z-[11] justify-end filters top-0 min-w-[100vw] p-0 ms:px-[1rem] xl:!px-[4.5rem] bg-white-999 flex flex-col w-full items-center ms:bg-black-150', { '!bg-[#EEDCBF]': validCapitalAccountsPath })}
      style={getStickyStyles({
        clientHeight,
        isTabletDevice: isTablet(),
        scrollHeight,
        scrollPosition,
        scrollProgress,
        validCapitalAccountsPath,
      })}
    >
      <div className='flex w-full max-w-[90rem] items-center justify-between'>
        {
          valid
            ? <NestedOrganizationsSelector scrollProgress={scrollPosition} />
            : <div className='flex items-baseline gap-[1rem] pb-[0.5rem] pl-[1rem]  pt-[1rem] ms:pl-0'>
              <GoBackButton to={isAccountRoute ? '/firm/capital-accounts' : '/firm/documents'} />
              <div className='flex gap-[0.5rem]'>
                {!mobile && !location.pathname.includes('settings') && <>
                  <h1
                    className={fundName ||
                      isOnlyOneFundAndInvestor(capitalAccountsData as
                         CapitalAccountFund[])
                      ? 'text-title-3 font-medium text-black-700' : 'm-0 text-title-3 font-medium'}
                  >
                    Digital capital account
                  </h1>
                  {fundName &&
                  <>
                    <h1 className='text-title-3 font-medium text-black-700' >/</h1>
                    <h1 className='text-title-3 font-medium'>{fundName || fundNameIfSingleFund}</h1>
                  </>}
                  {capitalAccountsData &&
                isOnlyOneFundAndInvestor(capitalAccountsData) &&
                 fundNameIfSingleFund &&
                 <>
                   <h1 className='text-title-3 font-medium text-black-700'>/</h1>
                   <h1 className='text-title-3 font-medium'>{fundName || fundNameIfSingleFund}</h1>
                 </>}
                </>}
              </div>
            </div>
        }
      </div>
    </div>
  );
};

export default Filters;
